/**
 * Active-fault sets for the exploded view, keyed by assembly id.
 *
 * Fault -> part mappings are explicit, reviewable tables rather than fuzzy
 * matching, because a wrong mapping points a technician at the wrong
 * component — the one failure mode of this feature that is worse than showing
 * nothing. Every entry is a deliberate assertion; unmapped codes are surfaced
 * to the user as unmapped instead of being silently dropped.
 *
 * Values are manifest node ids verified to exist in public/cad/{id}_assembly.json.
 */

import dummyAlerts from './dummyAlerts.js';
import { aircraftFaults } from './aviation/dummyAircraftData.js';
import { vacuumPumpFaults } from './industrial/dummyVacuumPumpData.js';

/** DTC ids from data/dummyAlerts.js -> ace_ev manifest ids. */
export const DTC_PART_MAP = {
  // Battery pack section 2 temperature-sensor discrepancy. The pack node
  // carries it, and module 02 is the specific section named by the alert.
  P1A08: ['S3_1_HV_BATTERY_PACK', 'U3_1_3_CELL_MODULE_02'],
  // CAN bus dropout — the two nodes that own vehicle network communication.
  U0001: ['S5_1_EVCU', 'S5_4_TELEMATICS_UNIT'],
  // Templates that can be raised by the demo scenarios.
  P0A0A: ['S3_1_HV_BATTERY_PACK'],
  P0A80: ['S3_1_HV_BATTERY_PACK'],
  P1003: ['S7_1_FRONT_R_WHEEL', 'S7_2_FRONT_R_BRAKE'],
  P0243: ['S2_1_TRACTION_MOTOR'],
};

/** Maintenance-message ids from data/aviation -> jet manifest ids. */
export const JET_FAULT_PART_MAP = {
  // Hot right main brake — the brake itself, not the whole gear leg.
  '32-42-01': ['U6_3_3_R_BRAKE_ASSEMBLY'],
  // EGT thermocouple spread sits in the turbine section of the right engine.
  '73-21-04': ['U5_2_4_R_HP_TURBINE', 'U5_2_5_R_LP_TURBINE'],
  '29-11-02': ['U8_2_2_RESERVOIR_B'],
  '28-41-07': ['U8_1_3_L_WING_FUEL_TANK', 'U8_1_4_R_WING_FUEL_TANK'],
  '34-10-03': ['U8_4_1_FORWARD_AVIONICS_BAY'],
};

/** Alarm ids from data/industrial -> vacuum_pump manifest ids (VACUUM_SPEC.md §9.1). */
export const VACUUM_FAULT_PART_MAP = {
  // Pump-or-process: the rate-of-rise check, wet oil, then the inlet restrictions.
  'V30-01': ['U3_1_1_INLET_ISOLATION_VALVE', 'U4_1_1_VESSEL_SHELL', 'U3_2_2_VALVE_FLAP', 'U3_1_3_INLET_SCREEN'],
  // Water in oil: the dead ballast solenoid and the locked thermostatic insert.
  'V40-01': ['U3_3_2_GAS_BALLAST_SOLENOID', 'U4_3_2_THERMOSTATIC_INSERT'],
  'V51-01': ['U5_1_1_SEPARATOR_ELEMENT', 'U4_5_2_SCAVENGE_STRAINER'],
  'V10-01': ['U6_1_1_OIL_COOLER_CORE', 'U4_3_2_THERMOSTATIC_INSERT', 'U4_2_2_OIL_FILTER_CARTRIDGE'],
  'V32-01': ['U3_2_2_VALVE_FLAP', 'U3_2_3_VALVE_SPRING', 'U3_2_4_VALVE_SEAT'],
  'V74-01': ['U4_2_2_OIL_FILTER_CARTRIDGE'],
};

export const FAULT_SETS = {
  ace_ev: {
    label: 'Active DTCs',
    title: 'Active diagnostic trouble codes',
    alerts: dummyAlerts,
    partMap: DTC_PART_MAP,
  },
  jet: {
    label: 'Active faults',
    title: 'Active maintenance messages',
    alerts: aircraftFaults,
    partMap: JET_FAULT_PART_MAP,
  },
  vacuum_pump: {
    label: 'Active alarms',
    title: 'Active pump alarms',
    alerts: vacuumPumpFaults,
    partMap: VACUUM_FAULT_PART_MAP,
  },
};

export const faultSetFor = (id) => FAULT_SETS[id] ?? null;

/* ------------------------------------------------------ fault by name */

const SEVERITY_RANK = { critical: 0, warning: 1, info: 2 };
const TITLE_STOP = new Set([
  'a', 'an', 'the', 'of', 'in', 'on', 'at', 'to', 'into', 'from', 'for', 'and', 'or', 'not', 'after', 'before',
  'is', 'are', 'it', 'by', 'with', 'h',
]);

/** Lower case, non-alphanumerics to single spaces: "Back-streaming!" -> "back streaming". */
const normalise = (text) => String(text ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const severityRank = (alert) => SEVERITY_RANK[String(alert?.severity || '').toLowerCase()] ?? 3;

/**
 * Which alert of an asset's fault set a phrase names ("the back-streaming
 * fault" -> 'V32-01'). Used when a voice "show the trace for …" arrives
 * without an id. Pure.
 *
 * Score 2 when an alias phrase occurs as whole words, else 1 when at least two
 * non-stop title words occur, else 0. Highest score wins; ties go to the more
 * severe alert, then list order. With no match (or no text) the most severe
 * active alert is returned. Returns an id or null.
 */
export function matchFault(assetId, text) {
  const alerts = faultSetFor(assetId)?.alerts ?? [];
  if (alerts.length === 0) return null;
  const q = normalise(text);
  const padded = ` ${q} `;
  const words = new Set(q.split(' ').filter(Boolean));

  let best = null;
  if (q) {
    alerts.forEach((alert, index) => {
      const aliases = Array.isArray(alert.aliases) ? alert.aliases : [];
      let score = 0;
      if (aliases.some((a) => normalise(a) && padded.includes(` ${normalise(a)} `))) {
        score = 2;
      } else {
        const titleWords = new Set(normalise(alert.title).split(' ').filter((w) => w && !TITLE_STOP.has(w)));
        let hits = 0;
        for (const w of titleWords) if (words.has(w)) hits += 1;
        if (hits >= 2) score = 1;
      }
      if (score === 0) return;
      const cand = { id: alert.id, score, sev: severityRank(alert), index };
      if (!best || cand.score > best.score
        || (cand.score === best.score && (cand.sev < best.sev || (cand.sev === best.sev && cand.index < best.index)))) {
        best = cand;
      }
    });
  }
  if (best) return best.id;

  let fallback = null;
  alerts.forEach((alert, index) => {
    if (alert.status !== 'active') return;
    const cand = { id: alert.id, sev: severityRank(alert), index };
    if (!fallback || cand.sev < fallback.sev) fallback = cand;
  });
  return fallback?.id ?? null;
}
